<script>
import moment from 'moment';
import AuditFilterFields from './AuditFilterFields.vue';
import FILTER_TYPE from '../common/enum/filter-types';

export default {
  name: 'AuditFilters',
  components: {
    AuditFilterFields,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    isFiltersVisible: true,
    addedFilters: [],
    requiredFilters: [
      FILTER_TYPE.COMPLETION_PERIOD,
    ],
    filterTypes: [
      { value: FILTER_TYPE.ACTION_TYPE, text: 'Action type' },
      { value: FILTER_TYPE.ACTOR_ID, text: 'Actor ID' },
      { value: FILTER_TYPE.ACTOR_EMAIL, text: 'Actor Email' },
      { value: FILTER_TYPE.ACTOR_TYPE, text: 'Actor type' },
      { value: FILTER_TYPE.APPLICATION_NAME, text: 'Application name' },
      { value: FILTER_TYPE.CLIENT_ID, text: 'Client ID' },
      { value: FILTER_TYPE.CREATION_PERIOD, text: 'Creation period' },
      { value: FILTER_TYPE.COMPLETION_PERIOD, text: 'Completion period' },
      { value: FILTER_TYPE.ENTITY_TYPE, text: 'Entity type' },
      { value: FILTER_TYPE.ENTITY_ID, text: 'Entity ID' },
      { value: FILTER_TYPE.TAGS, text: 'Tags' },
      { value: FILTER_TYPE.TARGET_ID, text: 'Target ID' },
      { value: FILTER_TYPE.TARGET_REFERENCE, text: 'Target reference' },
      { value: FILTER_TYPE.TARGET_TYPE, text: 'Target type' },
      { value: FILTER_TYPE.TRANSACTION_ID, text: 'Transaction ID' },
      { value: FILTER_TYPE.IS_BULK, text: 'Is bulk' },
    ],
  }),
  computed: {
    hiddenFilters() {
      return this.addedFilters.map((f) => (f.type));
    },
    requiredFiltersAdded() {
      return this.addedFilters.filter((filter) => (
        filter.required
      ));
    },
    optionalFiltersAdded() {
      return this.addedFilters.filter((filter) => (
        !filter.required
      ));
    },
  },
  beforeMount() {
    this.requiredFilters.forEach((filter) => {
      this.addFilter({
        type: filter,
        value: null,
        required: true,
      });
    });
  },
  mounted() {
    this.$emit('update-filters', this.processFilters());
  },
  methods: {
    addFilter(filter) {
      filter.id = `${Math.floor(Math.random() * 1e4)}${Date.now()}`;
      this.addedFilters.push(filter);
    },
    removeFilter(filterToRemove) {
      const indexToRemove = this.addedFilters.findIndex((filter) => (
        filter.id === filterToRemove.id
      ));

      if (indexToRemove < 0) {
        // eslint-disable-next-line no-console
        console.error('error on remove: index not found');
        this.$noty.error('Error trying to remove this filter');
        return;
      }

      this.addedFilters.splice(indexToRemove, 1);
    },
    updateFilter(updatedFilter) {
      const indexToUpdate = this.addedFilters.findIndex((filter) => (
        filter.id === updatedFilter.id
      ));

      this.addedFilters[indexToUpdate].type = updatedFilter.type;
      this.addedFilters[indexToUpdate].value = updatedFilter.value;
    },
    formatDateToISO(strDate) {
      return moment(strDate).toISOString();
    },
    processFilters() {
      const reducedFilters = [];

      this.addedFilters.forEach((filter) => {
        if (filter.type === FILTER_TYPE.TAGS) {
          reducedFilters[FILTER_TYPE.TAGS] = filter.value.split(',').map((t) => t.trim()).join(',');
          return;
        }

        if (filter.type === FILTER_TYPE.CREATION_PERIOD) {
          reducedFilters.created_at_from = this.formatDateToISO(filter.value[0]);
          reducedFilters.created_at_to = this.formatDateToISO(filter.value[1]);
          return;
        }

        if (filter.type === FILTER_TYPE.COMPLETION_PERIOD) {
          reducedFilters.completed_at_from = this.formatDateToISO(filter.value[0]);
          reducedFilters.completed_at_to = this.formatDateToISO(filter.value[1]);
          return;
        }

        if (filter.type === FILTER_TYPE.APPLICATION_NAME) {
          reducedFilters[FILTER_TYPE.APPLICATION_NAME] = this.updateApplicationNameValue(
            filter.value,
          );
          return;
        }

        reducedFilters[filter.type] = filter.value;
      }, {});

      return reducedFilters;
    },
    applyFilters() {
      this.$emit('apply-filters', this.processFilters());
    },
    updateApplicationNameValue(appliedValue) {
      const saveValue = appliedValue
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();

      switch (saveValue) {
        case 'clinical portal':
          return 'api-therapist';
        case 'ui admin':
          return 'api-administration';
        default:
          return appliedValue;
      }
    },
  },
};
</script>

<template>
  <div>
    <b-row>
      <b-col class="mt-4">
        <h4>
          API Logging
        </h4>
      </b-col>
    </b-row>

    <b-row class="d-flex justify-content-center">
      <b-col xl="10">
        <b-card no-body>
          <b-card-header class="d-flex justify-content-between">
            <b-card-title class="m-0 align-content-center">
              Filters
            </b-card-title>

            <div>
              <b-button
                v-b-toggle.filter-collapse-a.filter-collapse-b
                variant="outline-secondary"
              >
                {{ isFiltersVisible ? 'Hide' : 'Show' }} filters
                <feather
                  v-if="isFiltersVisible"
                  type="eye-off"
                />
                <feather
                  v-else
                  type="eye"
                />
              </b-button>
            </div>
          </b-card-header>

          <b-collapse
            id="filter-collapse-a"
            v-model="isFiltersVisible"
            :visible="isFiltersVisible"
          >
            <b-card-body>
              <b-row
                v-for="filter in requiredFiltersAdded"
                :key="filter.id"
                style="background-color: #f7f7f7;"
              >
                <b-col class="d-flex align-items-left flex-column">
                  <label
                    class="mb-2"
                    style="text-align: left; font-weight: bold;"
                  >
                    {{ filterTypes.find((f) => f.value === filter.type).text }}
                  </label>
                  <AuditFilterFields
                    :id="filter.id"
                    :type="filter.type"
                    :value="filter.value"
                    :required="filter.required"
                    @updated-filter="updateFilter"
                  />
                </b-col>
              </b-row>

              <hr v-if="optionalFiltersAdded.length">

              <b-row
                v-for="filter in optionalFiltersAdded"
                :key="filter.id"
              >
                <b-col class="d-flex align-items-left flex-column">
                  <AuditFilterFields
                    :id="filter.id"
                    :type="filter.type"
                    :value="filter.value"
                    :required="filter.required"
                    :filter-types="filterTypes"
                    :hidden-filters="hiddenFilters.filter((hidden) => hidden !== filter.type)"
                    @updated-filter="updateFilter"
                    @removed-filter="removeFilter"
                  />
                </b-col>
              </b-row>

              <hr v-if="addedFilters.length">

              <b-row>
                <b-col>
                  <AuditFilterFields
                    :filter-types="filterTypes"
                    :hidden-filters="hiddenFilters"
                    @added-filter="addFilter"
                  />
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>

          <b-collapse
            id="filter-collapse-b"
            :visible="isFiltersVisible"
          >
            <b-card-footer>
              <b-row>
                <b-col class="d-flex justify-content-end py-0">
                  <b-button
                    :disabled="isLoading"
                    variant="success"
                    @click.prevent="applyFilters"
                  >
                    Apply filters
                    <b-spinner
                      v-if="isLoading"
                      small
                      class="align-middle mr-2"
                    />
                    <feather
                      v-else
                      type="filter"
                    />
                  </b-button>
                </b-col>
              </b-row>
            </b-card-footer>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>
    <!-- /filters -->
  </div>
</template>
